import {
  Form,
  FormItemProps,
  Input,
  InputNumber,
  InputNumberProps,
  Select,
  Tooltip,
} from "antd"
import type { TextAreaProps } from "antd/es/input"
import { Label, RadioInput } from "components"

const { Option } = Select

export const CustomInput = ({
  label,
  name,
  placeholder,
  required = false,
  disabled = false,
  onChange,
  minLength,
  maxLength,
  type = "text",
  addonBefore,
  prefix,
  suffix,
  ...restProps
}: InputItemProps) => {
  return (
    <Form.Item
      label={
        <span className="text-padeBlack font-circular text-sm">{label}</span>
      }
      name={name}
      rules={
        restProps?.rules
          ? restProps?.rules
          : [{ required, message: "Please enter this field" }]
      }
      {...restProps}
    >
      <Input
        type={type}
        onChange={onChange}
        placeholder={placeholder}
        className="  text-neutral400"
        disabled={disabled}
        minLength={minLength}
        maxLength={maxLength}
        addonBefore={addonBefore}
        prefix={prefix}
        suffix={suffix}
      />
    </Form.Item>
  )
}

export const CustomInputNumber = ({
  label,
  name,
  placeholder,
  required = false,
  disabled = false,
  onChange,
  minLength,
  maxLength,
  type = "text",
  addonBefore,
  prefix,
  suffix,
  tooltip,
  ...restProps
}: NumberInputItemProps) => {
  return (
    <Form.Item
      label={
        <Tooltip title={tooltip as string}>
          <span className="text-padeBlack font-circular text-sm">{label}</span>
        </Tooltip>
      }
      name={name}
      rules={
        restProps?.rules
          ? restProps?.rules
          : [{ required, message: "Please enter this field" }]
      }
      {...restProps}
    >
      <InputNumber<number>
        type={type}
        onChange={onChange}
        placeholder={placeholder}
        className="  text-neutral400"
        disabled={disabled}
        minLength={minLength}
        maxLength={maxLength}
        addonBefore={addonBefore}
        prefix={prefix}
        suffix={suffix}
      />
    </Form.Item>
  )
}

export const CustomTextArea = ({
  label,
  name,
  maxLength,
  required = false,
  inputProps,
  ...restProps
}: TextAreaInputItemProps) => {
  return (
    <Form.Item
      label={
        <span className=" text-padeBlack text-sm font-circular ">{label}</span>
      }
      name={name}
      rules={[{ required, message: "Please enter this field!" }]}
      {...restProps}
    >
      <Input.TextArea
        maxLength={maxLength}
        {...inputProps}
        className="  text-neutral400"
      />
    </Form.Item>
  )
}

export const CustomSelect = ({
  label,
  name,
  required = false,
  showSearch = true,
  placeholder,
  options,
  value,
  onChange,
  onSelectClick,
  isLoading = false,
  addBottomSpace = false,
  mode,
  disabled = false,
  selectClassName,
  ...restProps
}: SelectItemProps) => {
  return (
    <Form.Item
      name={name}
      label={
        label ? (
          <span className="text-padeBlack font-circular text-sm">{label}</span>
        ) : null
      }
      rules={[{ required, message: "Please enter this field" }]}
      {...restProps}
    >
      <Select
        mode={mode}
        loading={isLoading}
        value={value}
        showSearch={showSearch}
        placeholder={placeholder}
        maxTagCount={7}
        optionLabelProp="label"
        onChange={onChange}
        allowClear
        onClick={onSelectClick}
        className={selectClassName}
        filterOption={(
          input: string,
          option?: {
            label: string
            value: string
            children: React.ReactNode
          }
        ) => {
          if (option) {
            const value = option.value || ""
            const children =
              typeof option.children === "string" ? option.children : ""
            const label = option.label || ""

            return (
              children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0 ||
              String(value)?.toLowerCase().indexOf(input?.toLowerCase()) >= 0 ||
              label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            )
          } else {
            return false
          }
        }}
        disabled={disabled}
      >
        {options?.map((option, index) => (
          <Option
            key={option.value}
            value={option.value}
            label={option.label}
          >
            <div
              style={{ whiteSpace: "normal" }}
              className=" text-neutral400 w-fit block"
            >
              {option.label}
            </div>
          </Option>
        ))}

        {(addBottomSpace || (options && options?.length >= 10)) &&
          ["first", "second"].map((item) => (
            <Option
              key={"empty" + item}
              disabled
              className="pointer-events-none"
            >
              {" "}
            </Option>
          ))}
      </Select>
    </Form.Item>
  )
}

const RadioGroup = ({
  options,
  value,
  onChange,
  disabled,
  direction = "column",
  name,
}: {
  options: RadioOption[] | undefined
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
  direction?: "row" | "column"
  name?: string
}) => (
  <div
    className={
      direction === "row" ? "flex items-center gap-4" : "w-full space-y-2"
    }
  >
    {options?.map((option) => (
      <div
        key={option.value}
        className="flex items-center gap-2"
      >
        <RadioInput
          name={name}
          id={`${name}-${option.value}`}
          value={option.value}
          disabled={disabled || option.disabled}
          checked={value === option.value}
          onChange={() => onChange?.(option.value)}
        />
        <Label
          htmlFor={`${name}-${option.value}`}
          className="mb-1.5"
        >
          {option.label}
        </Label>
      </div>
    ))}
  </div>
)

export const CustomRadioInput = ({
  label,
  name,
  required = false,
  options,
  direction = "column",
  disabled = false,
  onChange,
  ...restProps
}: RadioInputItemProps) => {
  return (
    <Form.Item
      label={
        label ? (
          <span className="text-padeBlack font-circular text-sm">{label}</span>
        ) : null
      }
      name={name}
      rules={
        restProps?.rules
          ? restProps?.rules
          : [{ required, message: "Please select an option" }]
      }
      {...restProps}
    >
      <RadioGroup
        options={options}
        disabled={disabled}
        direction={direction}
        name={name as string}
        onChange={onChange}
      />
    </Form.Item>
  )
}

type InputItemProps = FormItemProps & {
  label: React.ReactNode
  required?: boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  placeholder?: string
  disabled?: boolean
  minLength?: number
  maxLength?: number
  addonBefore?: string
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  type?:
    | "button"
    | "checkbox"
    | "color"
    | "date"
    | "datetime-local"
    | "email"
    | "file"
    | "hidden"
    | "image"
    | "month"
    | "number"
    | "password"
    | "radio"
    | "range"
    | "reset"
    | "search"
    | "submit"
    | "tel"
    | "text"
    | "time"
    | "url"
    | "week"
}

type NumberInputItemProps = FormItemProps &
  React.ComponentProps<typeof InputNumber>

type SelectItemProps = FormItemProps & {
  label: React.ReactNode
  required?: boolean
  showSearch?: boolean
  value?: string | number | string[] | number[] | null
  onChange?: (e: any) => void
  onSelectClick?: VoidFunction
  placeholder?: string
  options: { label: string; value: string | number }[] | undefined
  isLoading?: boolean
  addBottomSpace?: boolean
  disabled?: boolean
  mode?: "multiple" | "tags"
  selectClassName?: string
}

type TextAreaInputItemProps = FormItemProps & {
  label: React.ReactNode
  required?: boolean
  maxLength?: number
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  inputProps?: TextAreaProps
}

type RadioOption = {
  label: string
  value: string
  disabled?: boolean
}

type RadioInputItemProps = FormItemProps & {
  label?: React.ReactNode
  required?: boolean
  options: RadioOption[] | undefined
  disabled?: boolean
  direction?: "row" | "column"
  onChange?: (value: string) => void
}
